import React, {useCallback, useMemo, useState} from 'react'
import {FORKYGRAM_FOOTPRINTS_QUERY_LIMIT} from 'consts'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {TreeFotoTreeLeafFootprintTree} from 'types'
import {useDidMount, useDidUpdate, useHistory, useLocation} from 'utils'
import {useWindowMode} from 'windows'
import {ListNotLazy} from 'common/components'
import {GiftShopRootScreen} from 'pages/gift-shop'
import {
  ForkygramListGridItem,
  ForkygramTemplateListGridContent,
} from '../../template'
import {ForkygramFootprintPagination} from '../container'

export default function ForkygramFootprintDetailScreen() {
  const mode = useWindowMode()
  const location = useLocation('forkygram_footprint_all')
  const {translate} = useTranslation()
  const {memberId} = location.state
  const {page: pageQuery} = location.query
  const [count, setCount] = useState(0)
  const statePage = useState(Number(pageQuery) || 1)
  const rawPage = statePage[0]
  const column = mode === 'website' ? 5 : 4
  const history = useHistory()

  const pageCount = useMemo(
    () => Math.ceil(count / FORKYGRAM_FOOTPRINTS_QUERY_LIMIT),
    [count],
  )

  const handleUpdatePage = useCallback(
    (page: number) => {
      history.replaceQuery({
        ...location,
        path: 'forkygram_footprint_all',
        queryObject: {
          page,
        },
      })
    },
    [history, location],
  )

  const handleClickItem = useCallback(
    (item: TreeFotoTreeLeafFootprintTree) => {
      history.pushQuery({
        path: 'forkygram_footprint_detail',
        state: {
          memberId,
          tagId: item.tag_id,
          treeId: item.tree_id,
          treeName: item.tree_name,
          treeAlias: item.alias,
        },
        queryObject: {
          page: 1,
        },
      })
    },
    [history, memberId],
  )

  const handleRenderItem = useCallback(
    (item: TreeFotoTreeLeafFootprintTree) => (
      <ForkygramListGridItem
        borderColor="black"
        column={column}
        data={{
          contentType: item.post.content_type,
          url: item.url,
          thumbnail: item.url,
          caption: item.tree_name,
          isHidden: item.is_hidden,
          isPinned: !!item.pinned_at,
        }}
        onClickItem={() => handleClickItem(item)}
      />
    ),
    [column, handleClickItem],
  )

  const handleLoadData = useCallback(
    async (page: number, limit: number) => {
      const response = await requestData('tree_get_leaf_footprints', {
        useDefaultMessage: true,
        actionType: 'fetch',
        params: {page, limit, member_id: memberId},
        onRequestSuccess: ({status, data: {result}}) => {
          if (status === 200) setCount(result.count)
        },
      })
      return typeof response !== 'string' && response.status === 200
        ? response.data.result.footprints
        : null
    },
    [memberId],
  )

  const handleRenderContent = useMemo(
    () => (
      <ForkygramTemplateListGridContent
        innerMargin={0}
        header={false}
        backgroundColor="black"
        containerHeight="100%">
        <ForkygramFootprintPagination
          pageCount={pageCount}
          statePage={statePage}
        />
        <ListNotLazy
          renderItem={handleRenderItem}
          loadData={handleLoadData}
          limitPerPage={FORKYGRAM_FOOTPRINTS_QUERY_LIMIT}
          defaultPage={rawPage}
          page={rawPage}
          keyExtractor={(item: TreeFotoTreeLeafFootprintTree) => item.url}
          numColumns={column}
          scrollbar={false}
        />
      </ForkygramTemplateListGridContent>
    ),
    [column, handleLoadData, handleRenderItem, pageCount, rawPage, statePage],
  )

  useDidMount(() => {
    if (!memberId) {
      history.goBack()
    }
  })

  useDidUpdate(() => {
    handleUpdatePage(rawPage)
  }, [rawPage])

  return (
    <GiftShopRootScreen
      showBackIcon
      showBadgeIcon={false}
      darkBar
      darkHeader
      headerBorderColor="gray_8"
      headerTitle={translate('tree:fototreeFootprints')}
      headerSubtitle={translate('tree:fototreeFootprintsTreeCount', {count})}>
      {handleRenderContent}
    </GiftShopRootScreen>
  )
}
